'use client';

import { cn } from '@ahm/common-helpers';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { AIThinkingAnimation } from './ai-thinking-animation';
import { useAIPlannerStream } from '@/hooks/use-ai-planner-stream';
import { useAiFoodOrderStore, useAuthStore } from '@/store';
import { useAIPlannerStore } from '@/store/ai-planner';

// Simulated progress hook - smooth animation from 0 to ~95% over estimated time
function useSimulatedProgress(
  isComplete: boolean,
  estimatedDurationMs = 7500 // Average 7.5s (between 6-9s)
) {
  const [progress, setProgress] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  const animate = useCallback(() => {
    if (startTimeRef.current === null) {
      startTimeRef.current = Date.now();
    }

    const elapsed = Date.now() - startTimeRef.current;
    // Use easeOutQuart for natural deceleration - fast start, slow near end
    const t = Math.min(elapsed / estimatedDurationMs, 1);
    const eased = 1 - Math.pow(1 - t, 4);
    // Cap at 95% until complete
    const targetProgress = Math.min(eased * 95, 95);

    setProgress(targetProgress);

    if (targetProgress < 95) {
      rafRef.current = requestAnimationFrame(animate);
    }
  }, [estimatedDurationMs]);

  useEffect(() => {
    if (!isComplete) {
      rafRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isComplete, animate]);

  // When complete, smoothly animate to 100%
  useEffect(() => {
    if (isComplete && progress < 100) {
      const interval = setInterval(() => {
        setProgress((prev) => {
          const next = prev + 2;
          if (next >= 100) {
            clearInterval(interval);
            return 100;
          }
          return next;
        });
      }, 30);

      return () => clearInterval(interval);
    }
  }, [isComplete, progress]);

  return Math.round(progress);
}

// Processing steps configuration
const PROCESSING_STEPS = [
  {
    id: 1,
    title: 'Đang phân tích hồ sơ của bạn...',
    details: [
      '"Tuổi và giới tính',
      'Mục tiêu ăn uống',
      'Chế độ ăn',
      'Dị ứng',
      'Lưu ý đặc biệt cho đơn hàng"',
    ],
    progressRange: [0, 33],
  },
  {
    id: 2,
    title: 'Tính toán dinh dưỡng và đa dạng bữa ăn...',
    details: ['"Phù hợp với mục tiêu', 'Đa dạng mỗi ngày"'],
    progressRange: [33, 66],
  },
  {
    id: 3,
    title: 'Đang hoàn thiện kế hoạch bữa ăn...',
    details: ['"Lịch ăn trưa theo ngày đã sẵn sàng"'],
    progressRange: [66, 100],
  },
];

const HEADER_MESSAGES = [
  'Dựa trên các thông tin bạn cung cấp,',
  'AI đang lên kế hoạch bữa ăn cho bạn',
];

// Step indicator component
function StepIndicator({
  step,
  currentStep,
}: {
  step: number;
  currentStep: number;
}) {
  const isActive = step === currentStep;
  const isPassed = step < currentStep;

  return (
    <div
      className={cn(
        'size-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 backdrop-blur-xl',
        isActive &&
          'bg-[#ff8200] text-white ring-2 ring-[#ff8200]/30 ring-offset-2 ring-offset-transparent',
        isPassed && 'bg-[#ff8200] text-white',
        !isActive &&
          !isPassed &&
          'bg-white/10 text-white/40 border border-white/20'
      )}
    >
      {step}
    </div>
  );
}

// Processing step content
function ProcessingStepContent({
  step,
  isActive,
}: {
  step: (typeof PROCESSING_STEPS)[0];
  isActive: boolean;
}) {
  if (!isActive) return null;

  return (
    <div className="animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
      <p className="text-white/90 text-sm font-medium mb-2">{step.title}</p>
      <div className="text-white/50 text-xs space-y-0.5">
        {step.details.map((detail, index) => (
          <p
            key={index}
            className="animate-in fade-in-0 duration-300"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            {detail}
          </p>
        ))}
      </div>
    </div>
  );
}

export function AIProcessingScreen() {
  const preferences = useAIPlannerStore.use.preferences();
  const setIsGenerating = useAIPlannerStore.use.setIsGenerating();
  const setPlan = useAIPlannerStore.use.setPlan();
  const setCurrentStep = useAIPlannerStore.use.setCurrentStep();
  const { user } = useAuthStore();

  const cityId = useAiFoodOrderStore.use.city_id();
  const address = useAiFoodOrderStore.use.address();

  const [currentProcessingStep, setCurrentProcessingStep] = useState(1);

  const { currentPlan, status, error } = useAIPlannerStream({
    preferences,
    customerId: user?.cunstomerId ?? undefined,
    cityId: cityId ?? undefined,
    address: address ? { lat: address.lat, lng: address.lng } : undefined,
    token: user?.systemToken,
  });

  // Use simulated progress for smooth animation
  const isComplete = status === 'ready';
  const simulatedProgress = useSimulatedProgress(isComplete);

  // Calculate current step based on simulated progress
  useEffect(() => {
    if (simulatedProgress < 33) {
      setCurrentProcessingStep(1);
    } else if (simulatedProgress < 66) {
      setCurrentProcessingStep(2);
    } else {
      setCurrentProcessingStep(3);
    }
  }, [simulatedProgress]);

  // Update generating state based on status
  useEffect(() => {
    if (status === 'streaming') {
      setIsGenerating(true);
    } else if (status === 'ready') {
      setIsGenerating(false);
    }
  }, [status, setIsGenerating]);

  // Set plan when generation is complete
  useEffect(() => {
    if (currentPlan && status === 'ready') {
      setPlan(currentPlan);
    }
  }, [currentPlan, status, setPlan]);

  // Handle error - go back to delivery_days step
  useEffect(() => {
    if (error) {
      setCurrentStep('delivery_days');
      setIsGenerating(false);
    }
  }, [error, setCurrentStep, setIsGenerating]);

  // Get current step data
  const currentStepData = PROCESSING_STEPS.find(
    (s) => s.id === currentProcessingStep
  );

  return (
    <div className="flex flex-col items-center justify-center h-full min-h-[500px] py-8">
      {/* Title */}
      <h2 className="text-[#ff8200] font-bold text-xl mb-6">
        Phân tích kế hoạch ăn
      </h2>

      {/* Header Messages */}
      <div className="w-full max-w-xs text-center mb-8 px-4 space-y-1">
        {HEADER_MESSAGES.map((line, index) => (
          <p
            key={index}
            className="text-center text-white font-medium text-base"
          >
            {line}
          </p>
        ))}
      </div>

      {/* Step Progress Box */}
      <div className="w-full max-w-sm bg-white/5 ai-planner-button rounded-2xl p-6 mb-8">
        {/* Step Indicators */}
        <div className="flex justify-between items-center mb-6 px-4">
          {PROCESSING_STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <StepIndicator
                step={step.id}
                currentStep={currentProcessingStep}
              />
              {index < PROCESSING_STEPS.length - 1 && (
                <div
                  className={cn(
                    'flex-1 h-0.5 mx-2 transition-colors duration-300',
                    step.id < currentProcessingStep
                      ? 'bg-[#ff8200]/50'
                      : 'bg-white/10'
                  )}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <div className="min-h-[100px]">
          {currentStepData && (
            <ProcessingStepContent step={currentStepData} isActive={true} />
          )}
        </div>
      </div>

      {/* AI Animation */}
      <div className="size-40">
        <AIThinkingAnimation
          className="size-full"
          progress={simulatedProgress}
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-6 px-4 py-3 bg-red-50/10 border border-red-50/30 rounded-xl">
          <p className="text-red-40 text-sm text-center">
            Có lỗi xảy ra. Vui lòng thử lại.
          </p>
        </div>
      )}
    </div>
  );
}
